/**
 * Le planning des ressources réelles : qui est pris, quel jour, quelle demi-journée.
 *
 * *Demande de Chris, 03/09/2026 : « depuis la page d'affectation du plan de charge, ouvrir une
 * fenêtre supplémentaire qui contient sous forme de calendrier le planning de toutes les
 * ressources main d'œuvre et un deuxième planning de toutes les ressources matériel […] voir
 * jour par jour et demi-journée par demi-journée les heures déjà affectées sur les ressources,
 * en plus les congés et les maladies pour la main d'œuvre […] filtrées sur les ressources de
 * l'APO en cours, avec la possibilité d'afficher toutes les ressources. »*
 *
 * ## Aucun calcul neuf : une seconde LECTURE du plan de charge
 *
 * Ce module ne calcule ni heures, ni dates, ni conflits. Il reprend la réponse du planning
 * général — celle que le plan de charge affiche déjà — pour la ranger par **ressource réelle**
 * au lieu de par jour et par chantier. C'est la règle que `plan/general.ts` tient côté serveur :
 * *un seul calcul, plusieurs lectures*. Recompter les heures d'une personne à partir des barres
 * ferait un second calcul, qui finirait par annoncer 4 h là où le plan de charge en annonce 8,
 * et rien à l'écran ne dirait lequel a raison.
 *
 * ## Ce que porte une ressource, et non ce que porte une ligne
 *
 * Le besoin daté porte les heures de **tous les exemplaires réunis** — quatre chauffeurs une
 * demi-journée pèsent 14 h, pas 3,5 (règle du 16/08/2026). Un planning par ressource veut
 * l'inverse : ce que *cette* personne fait ce jour-là. La part d'un exemplaire est donc
 * `heures ÷ effectif déclaré`, exactement la division que l'infobulle du plan de charge affiche
 * déjà. Sans elle, une pelle affectée sur une ligne à deux pelles paraîtrait occupée seize
 * heures dans la journée.
 *
 * ## Une demi-journée n'est pas une supposition
 *
 * Les moments occupés **et leurs heures** viennent du besoin (`moments`), calculés par le
 * moteur d'après les barres et le chiffrage. Un besoin qui n'en porte aucun — une rotation de
 * transport, une livraison — occupe
 * « la journée » et **pas les deux demi-journées** : il a une heure limite, pas un matin.
 * L'étaler sur les deux moitiés ferait lire « pris toute la journée » là où un camion passe à
 * 15 h. Les deux natures sont donc rangées à part, et l'écran les dessine différemment.
 */
import { dec } from "@planif/moteur";
import { repartirSurLesMoments } from "./heuresParMoment";
import type { BesoinDate, ChantierAuPlan, Materiel, Salarie } from "./api";
import type { Absence } from "./AbsencesAGerer";

/** Ce qu'une ressource fait, sur un chantier, une demi-journée ou un jour donné. */
export type Occupation = {
  readonly chantierId: string;
  readonly apo: string;
  /**
   * La ligne de contre-étude d'où vient cette occupation, quand elle en porte une.
   *
   * Sert au saut vers le plan de charge (17/09/2026) et à rien d'autre ici : le planning des
   * ressources n'affiche pas d'identifiant. Elle voyage avec `rang` parce que c'est **le
   * couple (poste, rang) qui désigne une ligne dans la contre-étude**, l'identifiant ne
   * servant qu'à retrouver la rangée du plan de charge.
   */
  readonly ligneId: string | null;
  readonly rang: number;
  /** Le poste de la contre-étude — de quoi reconnaître le travail, pas seulement l'affaire. */
  readonly poste: string;
  /**
   * Le libellé du poste, avec son numéro.
   *
   * Les deux, et non le numéro seul : « APO250026 · _MOICDTEENG · 2 · 4 h » n'apprend rien à qui
   * ne connaît pas le devis par cœur (mesuré à l'écran le 03/09/2026). C'est le travail qu'on
   * cherche à reconnaître, pas sa place dans une numérotation.
   */
  readonly posteLibelle: string;
  readonly code: string;
  readonly nature: BesoinDate["nature"];
  /** La part d'UN exemplaire : les heures du besoin divisées par l'effectif déclaré. */
  readonly heures: string;
};

/** Une demi-journée, ou la journée entière pour ce qui n'a pas de moment. */
export type Moment = {
  readonly occupations: readonly Occupation[];
  readonly heures: string;
};

const MOMENT_VIDE: Moment = { occupations: [], heures: "0" };

/**
 * L'indisponibilité qui s'applique à cette ressource — et rien quand elle ne la concerne pas.
 *
 * **Une machine ne prend jamais un congé, une personne ne prend jamais une immobilisation.** Les
 * deux vivent dans la même carte, avec la même clé `<ressource>#<date>`, parce que le planning
 * pose exactement la même question aux deux : *est-elle disponible ce jour-là ?*
 *
 * Le tri par NATURE et non par provenance est ce qui garde la protection d'origine : les congés
 * sont rattachés à une fiche salarié, et un identifiant de machine qui coïnciderait marquerait une
 * pelle « en congé » — un fait que rien ne justifierait, et que personne ne saurait corriger. Un
 * essai tient cette règle depuis le 03/09/2026 ; il tient désormais les deux sens.
 */
function absenceDe(
  ressource: { readonly nature: "salarie" | "materiel" },
  absence: Absence | undefined,
): Absence | undefined {
  if (absence === undefined) return undefined;
  const pourUneMachine = absence.nature === "immobilisation";
  return pourUneMachine === (ressource.nature === "materiel") ? absence : undefined;
}

/** Ce qu'une ressource a sur les bras un jour donné. */
export type CelluleJour = {
  readonly matin: Moment;
  readonly apresMidi: Moment;
  /** Rotations et livraisons : un jour et une heure limite, jamais une demi-journée. */
  readonly journee: Moment;
  /**
   * L'absence de la personne ce jour-là — congé ou arrêt, sans motif.
   *
   * Toujours `undefined` pour une machine, et ce n'est pas un oubli : le référentiel maître ne
   * connaît aucune indisponibilité matériel (« les indisponibilités SAV sont la donnée de
   * l'appli planif, archive-roy ne les connaît pas »), et Planif n'en tient pas — un ordre de
   * réparation immobilisant ne porte ni période ni solde (migration 031, module SAV en V2).
   * L'écran le **dit** plutôt que d'afficher un tableau muet : une machine sans panne et une
   * machine dont on ignore les pannes se ressemblent trop.
   */
  readonly absence: Absence | undefined;
};

/** Une ligne du planning : une ressource réelle, et ses jours. */
export type LignePlanning = {
  readonly ressourceId: string;
  readonly nature: "salarie" | "materiel";
  readonly libelle: string;
  /** Le code générique de rattachement au référentiel, ou `null` s'il n'en porte pas. */
  readonly code: string | null;
  /** Cette ressource est-elle candidate au chantier regardé ? */
  readonly duChantier: boolean;
  readonly jours: ReadonlyMap<string, CelluleJour>;
  /** Les heures de la période, toutes affaires confondues. Zéro = jamais affectée. */
  readonly heures: string;
  /**
   * Combien d'occupations la période porte, toutes natures et toutes affaires confondues.
   *
   * **Distinct de `heures`, et c'est tout l'intérêt** *(Chris, 21/09/2026 : « pouvoir filtrer
   * uniquement sur les ressources utilisées pour restreindre les lignes »)* : une livraison et
   * une rotation ne portent aucune heure. Filtrer sur `heures > 0` aurait fait disparaître du
   * planning les camions qui ne font que des tournées — c'est-à-dire justement ceux que la vue
   * transport sert à regarder.
   */
  readonly occupations: number;
  /** Combien de jours portent à la fois le chantier regardé et un autre. */
  readonly conflits: number;
};

/* ---------------------------------------------------------------------------
 * La part d'un exemplaire
 * ------------------------------------------------------------------------- */

/**
 * Ce qu'une ressource porte, sur un besoin qui en attend plusieurs.
 *
 * `unites` est nul quand personne n'a déclaré d'effectif, ce qui n'est **pas** la même chose
 * qu'une unité (règle du 16/08/2026, tenue dans tout le produit) : la part est alors la
 * totalité, faute de savoir entre combien partager. C'est le repli honnête — supposer un
 * effectif de deux diviserait des heures sur la foi d'une devinette.
 */
export function partDUnExemplaire(heures: string | null, unites: number | null): string {
  if (heures === null) return "0";
  if (unites === null || unites <= 1) return dec(heures).toFixed();
  return dec(heures).div(unites).toFixed();
}

/* ---------------------------------------------------------------------------
 * Les candidats du chantier
 * ------------------------------------------------------------------------- */

/**
 * Les codes génériques que ce chantier demande.
 *
 * C'est ce qui définit « les ressources de l'APO » : non pas les personnes déjà désignées —
 * elles se comptent sur les doigts d'une main, et la fenêtre ne servirait alors qu'à relire ce
 * qu'on vient de saisir — mais **tous ceux que le référentiel rattache aux codes du chantier**
 * (décision de Chris, 03/09/2026). C'est la même règle que la liste déroulante du plan de
 * charge, qui propose les salariés du code du besoin.
 */
export function codesDuChantier(
  besoins: readonly BesoinDate[],
  chantierId: string,
): ReadonlySet<string> {
  const codes = new Set<string>();
  for (const besoin of besoins) {
    if (besoin.chantierId === chantierId) codes.add(besoin.code);
  }
  return codes;
}

/**
 * Les ressources réelles déjà affectées sur ce chantier, quel que soit leur code.
 *
 * Elles sont candidates **par le fait**, et non par le référentiel : le plan de charge autorise
 * à affecter hors du code quand le référentiel ne rattache personne (repli de `proposables`).
 * Sans cette liste, une machine réellement affectée sur l'APO regardée disparaîtrait de la vue
 * filtrée sur cette même APO — le pire endroit pour la perdre.
 */
export function affecteesSurLeChantier(
  besoins: readonly BesoinDate[],
  chantierId: string,
): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const besoin of besoins) {
    if (besoin.chantierId !== chantierId) continue;
    for (const affectation of besoin.affectations) ids.add(affectation.ressourceId);
  }
  return ids;
}

/* ---------------------------------------------------------------------------
 * Le rangement par ressource
 * ------------------------------------------------------------------------- */

type Brouillon = {
  matin: Occupation[];
  apresMidi: Occupation[];
  journee: Occupation[];
};

function brouillonDuJour(index: Map<string, Brouillon>, date: string): Brouillon {
  const existant = index.get(date);
  if (existant !== undefined) return existant;
  const neuf: Brouillon = { matin: [], apresMidi: [], journee: [] };
  index.set(date, neuf);
  return neuf;
}

function totaliser(occupations: readonly Occupation[]): Moment {
  if (occupations.length === 0) return MOMENT_VIDE;
  return {
    occupations,
    heures: occupations.reduce((somme, o) => somme.add(dec(o.heures)), dec(0)).toFixed(),
  };
}

/** Les occupations de chaque ressource, rangées par jour. */
function occupationsParRessource(
  besoins: readonly BesoinDate[],
  apoParChantier: ReadonlyMap<string, string>,
): ReadonlyMap<string, ReadonlyMap<string, Brouillon>> {
  const parRessource = new Map<string, Map<string, Brouillon>>();
  for (const besoin of besoins) {
    if (besoin.affectations.length === 0) continue;
    const commun = {
      chantierId: besoin.chantierId,
      apo: apoParChantier.get(besoin.chantierId) ?? besoin.chantierId,
      ligneId: besoin.ligneId,
      rang: besoin.rang,
      poste: besoin.poste,
      posteLibelle: besoin.posteLibelle,
      code: besoin.code,
      nature: besoin.nature,
    };
    const part = partDUnExemplaire(besoin.heures, besoin.unites);
    for (const affectation of besoin.affectations) {
      const jours = parRessource.get(affectation.ressourceId) ?? new Map<string, Brouillon>();
      parRessource.set(affectation.ressourceId, jours);
      const jour = brouillonDuJour(jours, besoin.date);
      if (besoin.moments.length === 0) {
        jour.journee.push({ ...commun, heures: part });
        continue;
      }
      /*
        **Les heures de chaque demi-journée viennent du serveur, jamais d'une division.**

        L'écran divisait le total du jour par le nombre de moments occupés : exact tant que
        toutes les demi-journées pesaient 4 h, faux depuis que les heures chiffrées se
        répartissent en remplissant (décision de Chris du 04/09/2026). Un matin de 4 h et un
        après-midi de 2 h s'affichaient « 3 h et 3 h » — un total juste fait de deux nombres
        faux.

        Ne reste ici que la division par l'EFFECTIF : le besoin porte les heures de tous les
        exemplaires réunis, la ligne du planning veut celles d'un seul.
      */
      for (const { position, heures } of repartirSurLesMoments(
        besoin.moments,
        besoin.unites ?? 1,
      )) {
        const dedans = { ...commun, heures };
        if (position === "matin") jour.matin.push(dedans);
        else jour.apresMidi.push(dedans);
      }
    }
  }
  return parRessource;
}

/** De quoi nommer une ressource et savoir à quel code elle se rattache. */
export type Ressource = {
  readonly id: string;
  readonly libelle: string;
  readonly code: string | null;
  readonly nature: "salarie" | "materiel";
};

/** Les salariés du référentiel, en ressources du planning. */
export function ressourcesDesSalaries(
  salaries: readonly Salarie[],
  designer: (salarie: Salarie) => string,
): readonly Ressource[] {
  return salaries.map((s) => ({
    id: s.id,
    libelle: designer(s),
    code: s.codeMo,
    nature: "salarie" as const,
  }));
}

/** Le parc, en ressources du planning. */
export function ressourcesDesMateriels(
  materiels: readonly Materiel[],
  designer: (materiel: Materiel) => string,
): readonly Ressource[] {
  return materiels.map((m) => ({
    id: m.id,
    libelle: designer(m),
    code: m.generique,
    nature: "materiel" as const,
  }));
}

/**
 * Range les ressources de part et d'autre de la case « transport ».
 *
 * *Demande de Chris, 03/09/2026 : « tu peux ne pas afficher tout ce qui concerne le transport,
 * donc `_MOICHAUF` ; pour la partie matériel tu peux également ne pas afficher les camions,
 * remorques etc. Par contre laisse bien les véhicules −3,5 t, VUL etc. qui sont à affecter avec
 * les personnels qui viennent sur chantier. »*
 *
 * **La liste ne s'écrit pas ici : elle se lit au référentiel.** C'est la case « transport » des
 * ressources génériques (migration 019), cochée sur les tracteurs, camions, bennes et chauffeurs
 * — et délibérément pas sur les `VU`, `VUL` et `VP`, *« du matériel roulant qui suit les
 * personnes »*. Écrire `_MOICHAUF` dans ce fichier serait le fork qui commence : ce sont les
 * codes de l'ERP d'une société, et aucune règle tirée du libellé n'aurait su séparer un camion
 * d'un fourgon.
 *
 * **Le compte des écartées traverse** : une liste amputée se lit exactement comme une liste
 * complète. Il sert aussi de diagnostic — si aucune ressource n'est écartée, c'est que personne
 * n'a coché la case, et l'écran le dit au lieu de laisser chercher pourquoi les camions sont
 * toujours là.
 */
export function separerLeTransport(
  ressources: readonly Ressource[],
  codesDeTransport: ReadonlySet<string>,
): { readonly transport: readonly Ressource[]; readonly horsTransport: readonly Ressource[] } {
  const transport: Ressource[] = [];
  const horsTransport: Ressource[] = [];
  for (const r of ressources) {
    // Une ressource sans code de rattachement va **hors transport** : rien ne dit qu'elle en
    // est, et la ranger là au doute la ferait disparaître de la vue de l'exploitation, qui est
    // celle où l'on tombe sur ce qu'on n'a pas su classer.
    if (r.code !== null && codesDeTransport.has(r.code)) transport.push(r);
    else horsTransport.push(r);
  }
  return { transport, horsTransport };
}

/**
 * Les ressources d'un côté, et combien l'autre côté en retient.
 *
 * *Le planning du transport est le miroir de celui de l'exploitation (Chris, 03/09/2026) : « les
 * plannings des ressources doivent afficher uniquement les ressources main d'œuvre transport puis
 * les ressources matériel transport […] pas la peine d'afficher les matériels transport −3,5 t
 * puisqu'ils sont affichés côté exploitation ».*
 *
 * **Et les −3,5 t sortent par construction** : `VU`, `VUL` et `VP` ne sont pas cochés
 * « transport » au référentiel, précisément parce qu'ils suivent les personnes. La même case
 * décide donc des deux vues, dans les deux sens — il n'y a aucune seconde règle à tenir, et
 * aucune ressource ne peut se retrouver dans les deux vues ni dans aucune.
 */
export function ressourcesDeLaVue(
  ressources: readonly Ressource[],
  codesDeTransport: ReadonlySet<string>,
  vue: "exploitation" | "transport",
): { readonly retenues: readonly Ressource[]; readonly ecartees: number } {
  const { transport, horsTransport } = separerLeTransport(ressources, codesDeTransport);
  return vue === "transport"
    ? { retenues: transport, ecartees: horsTransport.length }
    : { retenues: horsTransport, ecartees: transport.length };
}

/**
 * Le planning d'un ensemble de ressources, sur la période du plan.
 *
 * `chantierId` vaut `null` quand on regarde toutes les affaires : rien n'est alors « du
 * chantier », et aucun jour n'est en conflit — un conflit est un partage entre l'affaire qu'on
 * prépare et une autre, pas un fait absolu.
 *
 * **Les ressources sans une seule heure sont conservées**, et c'est tout l'objet de la fenêtre :
 * ce sont précisément celles-là qu'on cherche quand on affecte. Une vue qui ne montrerait que
 * les ressources occupées répondrait à la question inverse.
 */
export function construirePlanning({
  ressources,
  besoins,
  chantiers,
  absences,
  chantierId,
  codesRetenus,
  affectees,
}: {
  readonly ressources: readonly Ressource[];
  readonly besoins: readonly BesoinDate[];
  readonly chantiers: readonly ChantierAuPlan[];
  /** Les absences de la période, indexées `salarieId#date`. Vide pour le matériel. */
  readonly absences: ReadonlyMap<string, Absence>;
  readonly chantierId: string | null;
  /** Les codes du chantier regardé, ou `null` quand on regarde tout. */
  readonly codesRetenus: ReadonlySet<string> | null;
  /** Les ressources déjà affectées sur le chantier regardé, quel que soit leur code. */
  readonly affectees: ReadonlySet<string>;
}): readonly LignePlanning[] {
  const apoParChantier = new Map(chantiers.map((c) => [c.chantierId, c.apo]));
  const parRessource = occupationsParRessource(besoins, apoParChantier);

  const lignes: LignePlanning[] = [];
  for (const ressource of ressources) {
    const duChantier =
      codesRetenus === null
        ? false
        : affectees.has(ressource.id) ||
          (ressource.code !== null && codesRetenus.has(ressource.code));

    const jours = new Map<string, CelluleJour>();
    let heures = dec(0);
    let occupations = 0;
    let conflits = 0;
    for (const [date, brouillon] of parRessource.get(ressource.id) ?? []) {
      const matin = totaliser(brouillon.matin);
      const apresMidi = totaliser(brouillon.apresMidi);
      const journee = totaliser(brouillon.journee);
      heures = heures.add(dec(matin.heures)).add(dec(apresMidi.heures)).add(dec(journee.heures));
      const toutes = [...brouillon.matin, ...brouillon.apresMidi, ...brouillon.journee];
      occupations += toutes.length;
      if (
        chantierId !== null &&
        toutes.some((o) => o.chantierId === chantierId) &&
        toutes.some((o) => o.chantierId !== chantierId)
      ) {
        conflits += 1;
      }
      jours.set(date, {
        matin,
        apresMidi,
        journee,
        absence: absenceDe(ressource, absences.get(`${ressource.id}#${date}`)),
      });
    }

    /*
      Les jours d'absence entrent AUSSI quand la personne n'est affectée nulle part, et c'est le
      cas normal : quelqu'un en congés n'a rien sur les bras, et c'est justement ce qu'il faut
      voir avant de l'affecter. Ne marquer que les jours occupés ferait un planning où les
      absences n'apparaissent que là où elles gênent déjà.
    */
    for (const [cle, absence] of absences) {
      const separateur = cle.indexOf("#");
      if (separateur < 0) continue;
      if (cle.slice(0, separateur) !== ressource.id) continue;
      if (absenceDe(ressource, absence) === undefined) continue;
      const date = cle.slice(separateur + 1);
      if (jours.has(date)) continue;
      jours.set(date, {
        matin: MOMENT_VIDE,
        apresMidi: MOMENT_VIDE,
        journee: MOMENT_VIDE,
        absence,
      });
    }

    lignes.push({
      ressourceId: ressource.id,
      nature: ressource.nature,
      libelle: ressource.libelle,
      code: ressource.code,
      duChantier,
      jours,
      heures: heures.toFixed(),
      occupations,
      conflits,
    });
  }

  /*
    Le tri porte le sens de l'écran : le code d'abord — c'est le regroupement métier, un
    chauffeur se compare à un chauffeur —, puis le libellé. Trier par charge décroissante
    aurait fait sauter les lignes de place à chaque changement de période.

    Les ressources sans code passent en dernier : le caractère de rangement le plus haut les
    envoie en fin de tri sans qu'on ait à écrire un cas particulier dans le comparateur.
  */
  const SANS_CODE = "￿";
  return lignes.sort(
    (a, b) =>
      (a.code ?? SANS_CODE).localeCompare(b.code ?? SANS_CODE, "fr") ||
      a.libelle.localeCompare(b.libelle, "fr"),
  );
}

/**
 * Ce que cette cellule doit dire : occupée ici, ailleurs, ou les deux.
 *
 * Séparé du dessin pour être éprouvé seul, et parce que la même règle sert aux deux tableaux.
 * `null` = rien à dire, la cellule reste vide — un « 0 » se lirait comme une charge nulle
 * mesurée, alors que personne n'a rien posé.
 */
export function etatDuMoment(
  moment: Moment,
  chantierId: string | null,
): "ici" | "ailleurs" | "partage" | null {
  if (moment.occupations.length === 0) return null;
  if (chantierId === null) return "ailleurs";
  const ici = moment.occupations.some((o) => o.chantierId === chantierId);
  const ailleurs = moment.occupations.some((o) => o.chantierId !== chantierId);
  if (ici && ailleurs) return "partage";
  return ici ? "ici" : "ailleurs";
}

/**
 * L'affaire que cette case porte, quand elle n'en porte qu'UNE.
 *
 * *Demande de Chris, 21/09/2026 : « que toutes les affaires soient différenciées par couleur ».*
 *
 * `null` dans deux cas qui n'ont rien à voir mais qui appellent la même réponse — pas de couleur
 * d'affaire :
 *
 *  - **rien n'est posé** : il n'y a pas d'affaire à teinter ;
 *  - **plusieurs affaires le même moment** : la case ne peut pas porter deux fonds, et en
 *    choisir un ferait disparaître l'autre affaire de l'écran. Le partage garde donc son motif
 *    à lui, qui est précisément ce qu'il faut voir — c'est la même règle que le `partage` du
 *    plan de charge, vue par ressource.
 *
 * Séparée du dessin pour être éprouvée seule, comme `etatDuMoment` juste au-dessus.
 */
export function apoDuMoment(moment: Moment): string | null {
  const premier = moment.occupations[0];
  if (premier === undefined) return null;
  return moment.occupations.every((o) => o.apo === premier.apo) ? premier.apo : null;
}

/**
 * Ce qu'une occupation raconte dans une infobulle.
 *
 * L'APO d'abord : c'est la question du responsable — *où est cette personne ce jour-là ?* Le
 * poste vient après, parce qu'il ne se lit qu'une fois l'affaire connue. Une livraison annonce
 * sa nature au lieu d'un nombre d'heures : elle n'en porte aucune, et « 0 h » se lirait comme
 * une charge nulle plutôt que comme un camion à recevoir.
 */
export function direLOccupation(occupation: Occupation, heuresDites: string): string {
  const quoi = occupation.nature === "livraison" ? "livraison" : `${heuresDites} h`;
  const poste =
    occupation.posteLibelle === "" ? occupation.poste : `${occupation.poste} ${occupation.posteLibelle}`;
  return `${occupation.apo} · ${occupation.code} · ${poste} · ${quoi}`;
}
